import { c as _c } from "react/compiler-runtime";
import { Button } from '@/components/Atoms/Button/Button';
import { DetailsDropdown } from '@/components/Molecules/DetailsDropdown/DetailsDropdown';
import { FormField } from '@/components/Molecules/FormField/FormField';
import { SingleDropdown, SingleDropdownList } from '@/components/Molecules/SingleDropdown/SingleDropdown';
import { DateRangeFilter } from '@/components/Organisms/Filters/DateRangeFilter';
import { YearRangeFilter } from '@/components/Organisms/Filters/YearRangeFilter';
import { useSearchContext } from '@/contexts/SearchContext';
import useTranslation from '@/hooks/useTranslation';
import type { IFormChoice } from '@/interfaces';
import ArrowLeftIcon from '@bloomberg/web-icons-react/ArrowLeft';
import type { Filter, FilterBounds, FilterOption, FilterSelection } from 'bnef';
import type { ComponentProps, FC } from 'react';
import { useState } from 'react';
import type { FCl } from 'react-fc';
import styled, { css } from 'styled-components';

/*
    todo later:
     - Pass max/min dates as props
     - Need to be able to clear date inputs
     - Ensure Years inputs are cleared if a date/preset is set
     - Ensure Date inputs are cleared if years/preset is set
     - Can it work with progressive enhancement if you set date-type first and reload to set the field names?
 */

interface IProps {
  filter: Filter;
  presets?: IFormChoice[];
}
type DateFilterValue = string | FilterBounds | undefined;
export const getLabel = (activeFilter: FilterSelection, presets?: IProps['presets']) => {
  const from = (activeFilter as FilterBounds)?.gt ?? (activeFilter as FilterBounds)?.gte ?? null;
  const to = (activeFilter as FilterBounds)?.lt ?? (activeFilter as FilterBounds)?.lte ?? null;
  if (from && from === to) {
    return from;
  } else if (from && to) {
    return `${from ?? ''} - ${to ?? ''}`;
  } else if (from) {
    return `≥ ${from}`;
  } else if (to) {
    return `≤ ${to}`;
  }
  const selectedDateOption = presets?.find(option => option.value === activeFilter);
  if (selectedDateOption) {
    return selectedDateOption.label;
  }
  return null;
};
export const DatePicker: FCl<IProps> = t0 => {
  const $ = _c(60);
  const {
    className,
    filter,
    presets
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = t("common:Actions.selectNone");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      displayName: t1,
      value: undefined
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== filter.options || $[5] !== t2) {
    t3 = [t2, ...filter.options];
    $[4] = filter.options;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const dateOptions = t3;
  const hasMultipleOptions = filter.options.length > 1;
  const {
    activeFilters,
    setActiveFilter
  } = useSearchContext();
  let t4;
  if ($[7] !== activeFilters || $[8] !== dateOptions || $[9] !== filter.options[0] || $[10] !== hasMultipleOptions) {
    t4 = () => {
      if (!hasMultipleOptions) {
        return filter.options[0].value;
      }
      return dateOptions.find(option => Boolean(activeFilters[option.value]))?.value;
    };
    $[7] = activeFilters;
    $[8] = dateOptions;
    $[9] = filter.options[0];
    $[10] = hasMultipleOptions;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const [inputParam, setParam] = useState(t4);
  const activeFilter = activeFilters[inputParam] as DateFilterValue;
  let t5;
  if ($[12] !== t) {
    t5 = t("common:Actions.choose");
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let dateLabel = t5;
  if (inputParam && activeFilter) {
    let t6;
    if ($[14] !== activeFilter || $[15] !== presets) {
      t6 = getLabel(activeFilter, presets);
      $[14] = activeFilter;
      $[15] = presets;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    dateLabel = t6;
  }
  let t6;
  if ($[17] !== activeFilters || $[18] !== dateOptions || $[19] !== inputParam) {
    t6 = dateOptions.find(option_0 => option_0.value === inputParam || Boolean(activeFilters[option_0.value]));
    $[17] = activeFilters;
    $[18] = dateOptions;
    $[19] = inputParam;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const selectedOption = t6;
  let t7;
  if ($[21] !== filter.label || $[22] !== filter.nameKey || $[23] !== t) {
    t7 = filter.label ?? t(`SearchFilter.filterNames.${filter.nameKey}`);
    $[21] = filter.label;
    $[22] = filter.nameKey;
    $[23] = t;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let formFieldLabel = t7;
  if (!hasMultipleOptions && selectedOption) {
    formFieldLabel = selectedOption.displayName;
  }
  const t8 = filter.tooltip ?? null;
  let t9;
  if ($[25] !== activeFilter || $[26] !== dateOptions || $[27] !== hasMultipleOptions || $[28] !== inputParam || $[29] !== selectedOption?.displayName || $[30] !== selectedOption?.value || $[31] !== setActiveFilter) {
    t9 = hasMultipleOptions && <SingleDropdown title={selectedOption?.displayName} selectedValue={selectedOption?.value} name="date-type" onChange={option_1 => {
      const filterValue = activeFilter;
      if (activeFilter && inputParam) {
        setActiveFilter(inputParam, undefined);
      }
      setParam(option_1.value);
      if (option_1.value && filterValue) {
        setActiveFilter(option_1.value, filterValue);
      }
    }} options={dateOptions.map(_temp)} />;
    $[25] = activeFilter;
    $[26] = dateOptions;
    $[27] = hasMultipleOptions;
    $[28] = inputParam;
    $[29] = selectedOption?.displayName;
    $[30] = selectedOption?.value;
    $[31] = setActiveFilter;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const t10 = !inputParam;
  const t11 = !hasMultipleOptions;
  let t12;
  if ($[33] !== activeFilters || $[34] !== inputParam || $[35] !== presets) {
    t12 = presets?.find(option_2 => option_2.value === activeFilters[inputParam])?.value;
    $[33] = activeFilters;
    $[34] = inputParam;
    $[35] = presets;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== inputParam || $[38] !== setActiveFilter) {
    t13 = option_3 => {
      if (inputParam) {
        setActiveFilter(inputParam, option_3.value);
      }
    };
    $[37] = inputParam;
    $[38] = setActiveFilter;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== filter.componentConfig.maxDate || $[41] !== filter.componentConfig.minDate || $[42] !== inputParam || $[43] !== presets || $[44] !== t11 || $[45] !== t12 || $[46] !== t13) {
    t14 = <DateSelection includeNone={t11} selectedValue={t12} presets={presets} onChange={t13} name={inputParam} min={filter.componentConfig.minDate} max={filter.componentConfig.maxDate} />;
    $[40] = filter.componentConfig.maxDate;
    $[41] = filter.componentConfig.minDate;
    $[42] = inputParam;
    $[43] = presets;
    $[44] = t11;
    $[45] = t12;
    $[46] = t13;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  let t15;
  if ($[48] !== dateLabel || $[49] !== t10 || $[50] !== t14) {
    t15 = <DetailsDropdown dynamicLabel={true} label={dateLabel} disabled={t10}>{t14}</DetailsDropdown>;
    $[48] = dateLabel;
    $[49] = t10;
    $[50] = t14;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  let t16;
  if ($[52] !== t15 || $[53] !== t9) {
    t16 = <StyledFields>{t9}{t15}</StyledFields>;
    $[52] = t15;
    $[53] = t9;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  let t17;
  if ($[55] !== className || $[56] !== formFieldLabel || $[57] !== t16 || $[58] !== t8) {
    t17 = <FormField className={className} label={formFieldLabel} help={t8} asGroup={true}>{t16}</FormField>;
    $[55] = className;
    $[56] = formFieldLabel;
    $[57] = t16;
    $[58] = t8;
    $[59] = t17;
  } else {
    t17 = $[59];
  }
  return t17;
};
const DateSelection: FC<{
  name: string;
  presets: IFormChoice[];
  min?: string;
  max?: string;
  selectedValue?: string;
  onChange: (x: IFormChoice) => void;
  includeNone?: boolean;
}> = t0 => {
  const $ = _c(32);
  const {
    name,
    presets,
    selectedValue,
    onChange,
    min,
    max,
    includeNone
  } = t0;
  const {
    t
  } = useTranslation();
  const [view, setView] = useState();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Button kind="tertiary" onClick={() => setView(undefined)} icon={<ArrowLeftIcon />}>Back</Button>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const backButton = t1;
  let options;
  if ($[1] !== includeNone || $[2] !== presets || $[3] !== t) {
    let t2;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = () => setView("year");
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = () => setView("date");
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    options = [...presets, {
      value: "",
      label: t("common:Actions.chooseYears"),
      onChange: t2
    }, {
      value: "",
      label: t("common:Actions.chooseCustomDate"),
      onChange: t3
    }];
    if (includeNone) {
      options.unshift({
        label: t("common:Actions.selectNone"),
        value: ""
      });
    }
    $[1] = includeNone;
    $[2] = presets;
    $[3] = t;
    $[4] = options;
  } else {
    options = $[4];
  }
  const t2 = view ? true : undefined;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = css`
                    padding-block: var(--size-grid-half);
                `;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== name || $[9] !== onChange || $[10] !== options || $[11] !== selectedValue || $[12] !== t2) {
    t4 = <SingleDropdownList hidden={t2} name={name} options={options} selectedValue={selectedValue} onChange={onChange} css={t3} />;
    $[8] = name;
    $[9] = onChange;
    $[10] = options;
    $[11] = selectedValue;
    $[12] = t2;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const t5 = view !== "year" ? true : undefined;
  let t6;
  if ($[14] !== max || $[15] !== min || $[16] !== name) {
    t6 = <div><YearRangeFilter min={min} max={max} label="Year" name={name} /></div>;
    $[14] = max;
    $[15] = min;
    $[16] = name;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t5 || $[19] !== t6) {
    t7 = <SubPanel hidden={t5}>{backButton}{t6}</SubPanel>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const t8 = view !== "date" ? true : undefined;
  let t9;
  if ($[21] !== max || $[22] !== min || $[23] !== name) {
    t9 = <div><DateRangeFilter min={min} max={max} name={name} /></div>;
    $[21] = max;
    $[22] = min;
    $[23] = name;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t8 || $[26] !== t9) {
    t10 = <SubPanel hidden={t8}>{backButton}{t9}</SubPanel>;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t4 || $[30] !== t7) {
    t11 = <div>{t4}{t7}{t10}</div>;
    $[28] = t10;
    $[29] = t4;
    $[30] = t7;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
};
const SubPanel = styled.div`
    padding: var(--size-grid-half) var(--size-grid-unit) var(--size-grid-unit);
`;
const StyledFields = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--size-grid-half);
    > *:only-child {
        grid-column: 1 / -1;
    }
`;
function _temp(opt) {
  return {
    label: opt.displayName,
    value: opt.value
  };
}